import { Trophy, Sparkles, Target, ShieldCheck } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

const TITLE = "About Parlay Assistant | Free Betting Calculators & Odds Tools";
const DESCRIPTION =
  "Learn about Parlay Assistant — a free online resource offering fast, accurate sports betting calculators, odds converters, and educational tools.";

const features = [
  { icon: Trophy, label: "Free Parlay Calculator" },
  { icon: Sparkles, label: "Betting Odds Converter" },
  { icon: Target, label: "Educational Betting Resources" },
  { icon: ShieldCheck, label: "Sponsor Recommendations" },
];

export const AboutPage = () => {
  useSEO({ title: TITLE, description: DESCRIPTION, path: "/about" });

  return (
    <main
      data-testid="about-page"
      className="max-w-4xl mx-auto px-5 md:px-8 mt-10 md:mt-14"
    >
      <p className="font-display text-emerald-400 text-xs tracking-[0.25em] uppercase">
        Who We Are
      </p>
      <h1 className="mt-2 font-display text-white text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[1.02]">
        About Parlay Assistant
      </h1>
      <p className="mt-5 text-slate-300 text-base md:text-lg leading-relaxed max-w-3xl">
        Parlay Assistant is a free online resource built for sports bettors who want fast,
        accurate betting calculators and helpful tools. Our goal is to simplify sports betting
        math so users can quickly calculate payouts, convert betting odds, and better understand
        potential returns before placing a wager.
      </p>

      <section className="mt-10 md:mt-14 pa-card p-6 md:p-8">
        <h2 className="font-display text-slate-900 text-2xl md:text-3xl uppercase tracking-tight">
          What We Offer
        </h2>
        <p className="mt-2 text-slate-600 text-sm md:text-base">
          Every tool on Parlay Assistant runs entirely in your browser &mdash; no accounts, no
          downloads, no waiting.
        </p>
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <li
                key={f.label}
                className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50"
              >
                <span
                  aria-hidden="true"
                  className="w-9 h-9 rounded-lg inline-flex items-center justify-center"
                  style={{
                    background: "rgba(16,185,129,0.14)",
                    color: "#059669",
                    border: "1px solid rgba(16,185,129,0.3)",
                  }}
                >
                  <Icon size={16} />
                </span>
                <span className="font-medium text-slate-900 text-sm md:text-base">
                  {f.label}
                </span>
              </li>
            );
          })}
          <li className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
            <span
              aria-hidden="true"
              className="w-9 h-9 rounded-lg inline-flex items-center justify-center"
              style={{
                background: "rgba(16,185,129,0.14)",
                color: "#059669",
                border: "1px solid rgba(16,185,129,0.3)",
              }}
            >
              <Target size={16} />
            </span>
            <span className="font-medium text-slate-900 text-sm md:text-base">
              Sports Betting Tools
            </span>
          </li>
          <li className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
            <span
              aria-hidden="true"
              className="w-9 h-9 rounded-lg inline-flex items-center justify-center"
              style={{
                background: "rgba(16,185,129,0.14)",
                color: "#059669",
                border: "1px solid rgba(16,185,129,0.3)",
              }}
            >
              <Sparkles size={16} />
            </span>
            <span className="font-medium text-slate-900 text-sm md:text-base">
              Future Betting Calculators &amp; Utilities
            </span>
          </li>
        </ul>
      </section>

      <section className="mt-6 pa-card-dark p-6 md:p-8">
        <h2 className="font-display text-white text-2xl md:text-3xl uppercase tracking-tight">
          Our Mission
        </h2>
        <p className="mt-3 text-slate-300 text-sm md:text-base leading-relaxed">
          Our mission is to provide easy-to-use, accurate, and reliable sports betting tools that
          help users make more informed decisions while promoting responsible gambling.
        </p>
      </section>

      <section className="mt-6 pa-card p-6 md:p-8">
        <h2 className="font-display text-slate-900 text-2xl md:text-3xl uppercase tracking-tight">
          Responsible Gambling
        </h2>
        <p className="mt-3 text-slate-700 text-sm md:text-base leading-relaxed">
          Parlay Assistant is an informational and educational resource only. We do not operate a
          sportsbook, accept wagers, or guarantee betting outcomes. Always bet responsibly and
          only within your local laws.
        </p>
      </section>
    </main>
  );
};

export default AboutPage;
